import React, { useEffect, useState } from "react";

import { practiceSchedulesService } from "api/services/practice-schedules.service";
import ContentHeader from "components/Layout/ContentHeader";
import PracticeScheduleDetailModal from "components/schedule-group/PracticeScheduleDetailModal";
import ReservationListModal, { type ReservationCountChange } from "components/schedule-group/ReservationListModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Tabs from "components/UI/Tabs";
import { extractList, getApiErrorMessage } from "utils/api-error";
import { SCHEDULE_SORT_OPTIONS, SCHEDULE_STATUS_TABS } from "utils/constant/schedule";
import { sortSchedulesByDate } from "utils/schedule";

import type { PracticeSchedule, PracticeStatus } from "api/types/practice-schedules.types";
import type { ScheduleSortOrder } from "utils/type/schedule";
import "pages/AdminPage.scss";
import "./PracticeScheduleList.scss";

const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "draft"> = {
  OPEN: "active",
  CLOSED: "draft",
  CANCELLED: "rejected",
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const PracticeScheduleList: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [items, setItems] = useState<PracticeSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(getCurrentMonth());
  const [sortOrder, setSortOrder] = useState<ScheduleSortOrder>("latest");

  // 완료 처리 모달
  const [completeModal, setCompleteModal] = useState<{
    open: boolean;
    item: PracticeSchedule | null;
  }>({ open: false, item: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // 유료 전환은 연습반 상세 모달의 [관리자 작업] 섹션으로 이동했습니다.
  // (강사가 앱에서 직접 유료 전환을 신청하는 것이 기본 흐름이고,
  // 어드민의 강제 전환은 예외적인 동작이므로 목록 컬럼에서는 숨김 처리)

  // 예약자 목록 모달
  const [reservationModal, setReservationModal] = useState<{
    open: boolean;
    scheduleId: number | null;
    title: string;
  }>({ open: false, scheduleId: null, title: "" });

  const openReservations = (schedule: PracticeSchedule) => {
    setReservationModal({
      open: true,
      scheduleId: schedule.id,
      title: `${schedule.title} - ${schedule.date}`,
    });
  };

  const closeReservations = () => {
    setReservationModal({ open: false, scheduleId: null, title: "" });
  };

  // 강제 예약 / 취소 시 리스트의 예약/대기 카운트를 즉시 반영 (낙관적 업데이트)
  const handleReservationChanged = (change: ReservationCountChange) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === change.scheduleId
          ? {
              ...it,
              current_bookings: Math.max(0, it.current_bookings + change.confirmedDelta),
              waiting_count: Math.max(0, it.waiting_count + change.waitingDelta),
            }
          : it,
      ),
    );
  };

  // 연습반 상세 모달
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    scheduleId: number | null;
  }>({ open: false, scheduleId: null });

  const openDetail = (schedule: PracticeSchedule) => {
    setDetailModal({ open: true, scheduleId: schedule.id });
  };

  const closeDetail = () => {
    setDetailModal({ open: false, scheduleId: null });
  };

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};
      const pad = (n: number) => String(n).padStart(2, "0");
      const [year, month] = dateFilter.split("-").map(Number);
      const lastDate = new Date(year, month, 0).getDate();
      params.startDate = `${year}-${pad(month)}-01`;
      params.endDate = `${year}-${pad(month)}-${pad(lastDate)}`;
      const res = await practiceSchedulesService.list(params);
      setItems(extractList<PracticeSchedule>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const filteredItems = items.filter((item) => {
    if (activeTab !== "all" && item.status !== activeTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return item.title.toLowerCase().includes(q) || item.instructor_name.toLowerCase().includes(q) || item.location_name.toLowerCase().includes(q);
  });

  const sortedItems = sortSchedulesByDate(filteredItems, sortOrder);

  const handleComplete = async () => {
    if (!completeModal.item) return;
    setActionLoading(true);
    try {
      await practiceSchedulesService.complete(completeModal.item.id);
      setCompleteModal({ open: false, item: null });
      fetchList();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const columns: Column<PracticeSchedule>[] = [
    { key: "title", title: "제목" },
    { key: "instructor_name", title: "강사" },
    { key: "date", title: "날짜" },
    {
      key: "start_time",
      title: "시간",
      // width: "120px",
      render: (v, row) => `${String(v).slice(0, 5)} ~ ${String(row.end_time).slice(0, 5)}`,
    },
    { key: "location_name", title: "장소" },
    {
      key: "status",
      title: "상태",
      // width: "70px",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{String(v)}</Badge>,
    },
    {
      key: "current_bookings",
      title: "예약/정원",
      // width: "80px",
      render: (v, row) => `${v}/${row.capacity}`,
    },

    // {
    //   key: "pricing_type",
    //   title: "유형",
    //   // width: "70px",
    //   render: (v) => (
    //     <Badge variant={PRICING_BADGE[String(v)] ?? "draft"}>
    //       {String(v) === "FREE" ? "무료" : "유료"}
    //     </Badge>
    //   ),
    // },

    {
      key: "current_price",
      title: "가격(유료)",
      // width: "90px",
      render: (v) => (v ? `${Number(v).toLocaleString()}원` : "-"),
    },

    {
      key: "reservations",
      title: "예약자",
      width: "90px",
      render: (_, row) => (
        <div className="practice-actions">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              openReservations(row);
            }}
          >
            보기
          </Button>
        </div>
      ),
    },

    // 유료전환 컬럼은 상세 모달의 [관리자 작업] 섹션으로 이동
    // {
    //   key: "actions",
    //   title: "유료전환",
    //   render: (_, row) => (
    //     <div className="practice-actions">
    //       {row.pricing_type === "FREE" && row.status === "OPEN" && (
    //         <Button
    //           size="sm"
    //           variant="outline"
    //           onClick={() => setConvertModal({ open: true, item: row })}
    //         >
    //           전환하기
    //         </Button>
    //       )}
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="연습반 일정" description="연습 스케줄을 관리하고 완료 처리합니다." />

      <div className="admin-page__card">
        <Tabs items={SCHEDULE_STATUS_TABS} activeKey={activeTab} onChange={(k) => setActiveTab(k as PracticeStatus | "all")} />

        <div className="admin-page__filter">
          <Input placeholder="제목, 강사명, 장소 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
          <input type="month" value={dateFilter} onChange={(e) => setDateFilter(e.target.value || getCurrentMonth())} className="schedule-date-input" />
          <Select aria-label="정렬" options={SCHEDULE_SORT_OPTIONS} value={sortOrder} onChange={(e) => setSortOrder(e.target.value as ScheduleSortOrder)} />
          {dateFilter !== getCurrentMonth() && (
            <Button size="sm" variant="ghost" onClick={() => setDateFilter(getCurrentMonth())}>
              이번 달
            </Button>
          )}
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={sortedItems} rowKey="id" onRowClick={openDetail} />}
      </div>

      {/* 완료 처리 모달 */}
      <Modal
        open={completeModal.open}
        title="연습 완료 처리"
        width="440px"
        onClose={() => setCompleteModal({ open: false, item: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setCompleteModal({ open: false, item: null })}>
              취소
            </Button>
            <Button variant="primary" onClick={handleComplete} disabled={actionLoading}>
              {actionLoading ? "처리 중..." : "완료 처리"}
            </Button>
          </div>
        }
      >
        <p className="confirm-text">
          <strong>"{completeModal.item?.title}"</strong> 연습을 완료 처리하시겠습니까?
        </p>
      </Modal>

      {/* 유료 전환 모달은 상세 모달의 [관리자 작업] 섹션으로 통합되었습니다. */}

      <ReservationListModal open={reservationModal.open} scheduleId={reservationModal.scheduleId} scheduleType="practice" title={reservationModal.title} onClose={closeReservations} onChanged={handleReservationChanged} />

      <PracticeScheduleDetailModal open={detailModal.open} scheduleId={detailModal.scheduleId} onClose={closeDetail} onSaved={fetchList} />
    </div>
  );
};

export default PracticeScheduleList;
